import React, { useEffect, useState } from 'react';
import { ArrowRightIcon } from 'lucide-react';
const subheadings = ['> Partners in Automation.', '> Systems, Not Shortcuts.', '> Outcomes on Autopilot.', '> Compounding Gains, Not One-Off Wins.'];
export function Hero() {
  const [currentLineIndex, setCurrentLineIndex] = useState(0);
  const [displayedLines, setDisplayedLines] = useState<string[]>([]);
  const [currentText, setCurrentText] = useState('');
  const [isTyping, setIsTyping] = useState(true);
  const [showCursor, setShowCursor] = useState(true);
  // Cursor blink effect
  useEffect(() => {
    const cursorInterval = setInterval(() => {
      setShowCursor(prev => !prev);
    }, 530);
    return () => clearInterval(cursorInterval);
  }, []);
  // Main typing animation logic
  useEffect(() => {
    if (currentLineIndex >= subheadings.length) {
      // All lines typed, pause then clear
      const clearTimer = setTimeout(() => {
        setDisplayedLines([]);
        setCurrentText('');
        setCurrentLineIndex(0);
        setIsTyping(true);
      }, 2000);
      return () => clearTimeout(clearTimer);
    }
    if (isTyping) {
      const targetText = subheadings[currentLineIndex];
      if (currentText.length < targetText.length) {
        // Type next character
        const typeTimer = setTimeout(() => {
          setCurrentText(targetText.slice(0, currentText.length + 1));
        }, 50);
        return () => clearTimeout(typeTimer);
      } else {
        // Line complete, pause then move to next
        const pauseTimer = setTimeout(() => {
          setDisplayedLines(prev => [...prev, currentText]);
          setCurrentText('');
          setCurrentLineIndex(prev => prev + 1);
        }, 1500);
        return () => clearTimeout(pauseTimer);
      }
    }
  }, [currentText, currentLineIndex, isTyping]);
  return <div className="relative w-full min-h-screen bg-black overflow-hidden flex items-center justify-center" data-id="element-300">
      {/* Animated grid background */}
      <div className="absolute inset-0 opacity-10" style={{
      backgroundImage: 'linear-gradient(#ffa500 1px, transparent 1px), linear-gradient(90deg, #ffa500 1px, transparent 1px)',
      backgroundSize: '50px 50px',
      animation: 'gridMove 20s linear infinite'
    }} data-id="element-301" />

      {/* Floating particles */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none" data-id="element-302">
        {[...Array(20)].map((_, i) => <div key={i} className="absolute w-1 h-1 bg-amber-500 rounded-full animate-float" style={{
        left: `${Math.random() * 100}%`,
        top: `${Math.random() * 100}%`,
        animationDelay: `${Math.random() * 5}s`,
        animationDuration: `${5 + Math.random() * 10}s`,
        opacity: Math.random() * 0.5 + 0.3
      }} data-id="element-303" />)}
        {[...Array(15)].map((_, i) => <div key={`green-${i}`} className="absolute w-1 h-1 bg-green-500 rounded-full animate-float" style={{
        left: `${Math.random() * 100}%`,
        top: `${Math.random() * 100}%`,
        animationDelay: `${Math.random() * 5}s`,
        animationDuration: `${5 + Math.random() * 10}s`,
        opacity: Math.random() * 0.5 + 0.3
      }} data-id="element-304" />)}
      </div>

      {/* Terminal header bar */}
      <div className="absolute top-0 left-0 right-0 h-12 bg-zinc-900 border-b-2 border-amber-600 flex items-center px-6 z-20" data-id="element-305">
        <div className="flex items-center gap-2" data-id="element-306">
          <div className="w-3 h-3 rounded-full bg-red-500" data-id="element-307" />
          <div className="w-3 h-3 rounded-full bg-amber-500" data-id="element-308" />
          <div className="w-3 h-3 rounded-full bg-green-500" data-id="element-309" />
        </div>
        <div className="flex-1 text-center" data-id="element-310">
          <span className="text-green-400 font-mono text-sm" data-id="element-311">
            Dilbert_Ratio_Group
          </span>
        </div>
      </div>

      {/* Main content */}
      <div className="relative z-10 container mx-auto px-6 pt-20" data-id="element-312">
        <div className="grid lg:grid-cols-5 gap-12 items-center max-w-7xl mx-auto" data-id="element-313">
          {/* Left side - Content */}
          <div className="lg:col-span-3 space-y-8" data-id="element-314">
            {/* Headline */}
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-amber-500 leading-tight" data-id="element-315">
              Your{' '}
              <span className="text-green-400 glow-text" data-id="element-316">
                Fractional AI Automation Team
              </span>{' '}
              for Revenue, Efficiency, and Consistency at Scale
            </h1>

            {/* Terminal output area */}
            <div className="bg-black border-2 border-green-500 p-6 min-h-[200px] font-mono text-lg" data-id="element-317">
              {displayedLines.map((line, index) => <div key={index} className="text-green-400 mb-2" data-id="element-318">
                  {line}
                </div>)}
              {currentLineIndex < subheadings.length && <div className="text-green-400" data-id="element-319">
                  {currentText}
                  {showCursor && <span className="inline-block w-2 h-5 bg-green-400 ml-1" data-id="element-320" />}
                </div>}
            </div>

            {/* CTA */}
            <a href="https://www.linkedin.com/in/dawsonsewell" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-3 bg-amber-600 hover:bg-amber-500 text-black font-bold py-5 px-10 transition-all duration-200 border-4 border-amber-400 shadow-[0_0_30px_rgba(255,165,0,0.6)] hover:shadow-[0_0_50px_rgba(255,165,0,0.9)] group text-xl" data-id="element-321">
              <span className="tracking-wider" data-id="element-322">START THE CONVERSATION</span>
              <ArrowRightIcon className="w-6 h-6 group-hover:translate-x-2 transition-transform" data-id="element-323" />
            </a>
          </div>

          {/* Right side - Workflow visualization */}
          <div className="lg:col-span-2 relative h-[500px] hidden lg:block" data-id="element-324">
            <svg className="w-full h-full" viewBox="0 0 400 500" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-325">
              {/* Animated connections */}
              <g className="animate-pulse-slow" data-id="element-326">
                <line x1="100" y1="100" x2="200" y2="150" stroke="#22c55e" strokeWidth="2" strokeDasharray="5,5" className="animate-dash" data-id="element-327" />
                <line x1="200" y1="150" x2="300" y2="100" stroke="#f59e0b" strokeWidth="2" strokeDasharray="5,5" className="animate-dash" style={{
                animationDelay: '0.5s'
              }} data-id="element-328" />
                <line x1="100" y1="250" x2="200" y2="250" stroke="#22c55e" strokeWidth="2" strokeDasharray="5,5" className="animate-dash" style={{
                animationDelay: '1s'
              }} data-id="element-329" />
                <line x1="200" y1="250" x2="300" y2="250" stroke="#f59e0b" strokeWidth="2" strokeDasharray="5,5" className="animate-dash" style={{
                animationDelay: '1.5s'
              }} data-id="element-330" />
                <line x1="100" y1="400" x2="200" y2="350" stroke="#22c55e" strokeWidth="2" strokeDasharray="5,5" className="animate-dash" style={{
                animationDelay: '2s'
              }} data-id="element-331" />
                <line x1="200" y1="350" x2="300" y2="400" stroke="#f59e0b" strokeWidth="2" strokeDasharray="5,5" className="animate-dash" style={{
                animationDelay: '2.5s'
              }} data-id="element-332" />
              </g>

              {/* Nodes */}
              <g data-id="element-333">
                <circle cx="100" cy="100" r="12" fill="#22c55e" className="animate-pulse" data-id="element-334" />
                <circle cx="200" cy="150" r="12" fill="#f59e0b" className="animate-pulse" style={{
                animationDelay: '0.3s'
              }} data-id="element-335" />
                <circle cx="300" cy="100" r="12" fill="#22c55e" className="animate-pulse" style={{
                animationDelay: '0.6s'
              }} data-id="element-336" />
                <circle cx="100" cy="250" r="12" fill="#f59e0b" className="animate-pulse" style={{
                animationDelay: '0.9s'
              }} data-id="element-337" />
                <circle cx="200" cy="250" r="12" fill="#22c55e" className="animate-pulse" style={{
                animationDelay: '1.2s'
              }} data-id="element-338" />
                <circle cx="300" cy="250" r="12" fill="#f59e0b" className="animate-pulse" style={{
                animationDelay: '1.5s'
              }} data-id="element-339" />
                <circle cx="100" cy="400" r="12" fill="#22c55e" className="animate-pulse" style={{
                animationDelay: '1.8s'
              }} data-id="element-340" />
                <circle cx="200" cy="350" r="12" fill="#f59e0b" className="animate-pulse" style={{
                animationDelay: '2.1s'
              }} data-id="element-341" />
                <circle cx="300" cy="400" r="12" fill="#22c55e" className="animate-pulse" style={{
                animationDelay: '2.4s'
              }} data-id="element-342" />
              </g>

              {/* Flowing particles */}
              <g data-id="element-343">
                <circle cx="150" cy="125" r="4" fill="#22c55e" className="animate-flow-1" data-id="element-344" />
                <circle cx="250" cy="125" r="4" fill="#f59e0b" className="animate-flow-2" data-id="element-345" />
                <circle cx="150" cy="250" r="4" fill="#22c55e" className="animate-flow-3" data-id="element-346" />
                <circle cx="250" cy="250" r="4" fill="#f59e0b" className="animate-flow-4" data-id="element-347" />
              </g>
            </svg>
          </div>
        </div>
      </div>

      <style data-id="element-348">{`
        @keyframes gridMove {
          0% {
            transform: translate(0, 0);
          }
          100% {
            transform: translate(50px, 50px);
          }
        }
        @keyframes float {
          0%, 100% {
            transform: translateY(0) translateX(0);
          }
          50% {
            transform: translateY(-100px) translateX(50px);
          }
        }
        @keyframes pulse-slow {
          0%, 100% {
            opacity: 0.4;
          }
          50% {
            opacity: 1;
          }
        }
        @keyframes glow-pulse {
          0%, 100% {
            text-shadow: 0 0 20px rgba(34, 197, 94, 0.8);
          }
          50% {
            text-shadow: 0 0 30px rgba(34, 197, 94, 1), 0 0 40px rgba(34, 197, 94, 0.6);
          }
        }
        @keyframes dash {
          0% {
            stroke-dashoffset: 100;
          }
          100% {
            stroke-dashoffset: 0;
          }
        }
        @keyframes flow-1 {
          0% {
            cx: 100;
            cy: 100;
            opacity: 0;
          }
          50% {
            opacity: 1;
          }
          100% {
            cx: 200;
            cy: 150;
            opacity: 0;
          }
        }
        @keyframes flow-2 {
          0% {
            cx: 200;
            cy: 150;
            opacity: 0;
          }
          50% {
            opacity: 1;
          }
          100% {
            cx: 300;
            cy: 100;
            opacity: 0;
          }
        }
        @keyframes flow-3 {
          0% {
            cx: 100;
            cy: 250;
            opacity: 0;
          }
          50% {
            opacity: 1;
          }
          100% {
            cx: 200;
            cy: 250;
            opacity: 0;
          }
        }
        @keyframes flow-4 {
          0% {
            cx: 200;
            cy: 250;
            opacity: 0;
          }
          50% {
            opacity: 1;
          }
          100% {
            cx: 300;
            cy: 250;
            opacity: 0;
          }
        }
        .animate-float {
          animation: float linear infinite;
        }
        .animate-pulse-slow {
          animation: pulse-slow 3s ease-in-out infinite;
        }
        .animate-dash {
          animation: dash 2s linear infinite;
        }
        .animate-flow-1 {
          animation: flow-1 3s ease-in-out infinite;
        }
        .animate-flow-2 {
          animation: flow-2 3s ease-in-out infinite 0.5s;
        }
        .animate-flow-3 {
          animation: flow-3 3s ease-in-out infinite 1s;
        }
        .animate-flow-4 {
          animation: flow-4 3s ease-in-out infinite 1.5s;
        }
        .glow-text {
          animation: glow-pulse 2s ease-in-out infinite;
        }
      `}</style>
    </div>;
}